import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  MapPin, TreePine, Clock, CheckCircle, Loader2, ArrowLeft, 
  Dog, Fence, Droplets, Car, Wifi, Camera, Sun, Moon
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Common dog park amenities
const AMENITIES = [
  { id: 'fenced', label: 'Fully Fenced', icon: Fence },
  { id: 'off_leash', label: 'Off-Leash Area', icon: Dog },
  { id: 'water_fountain', label: 'Water Fountain', icon: Droplets },
  { id: 'waste_stations', label: 'Waste Stations', icon: CheckCircle },
  { id: 'parking', label: 'Parking Available', icon: Car },
  { id: 'benches', label: 'Benches/Seating', icon: TreePine },
  { id: 'shade', label: 'Shaded Areas', icon: TreePine },
  { id: 'lights', label: 'Lighting', icon: Sun },
  { id: 'separate_areas', label: 'Separate Small/Large Dog Areas', icon: Dog },
  { id: 'agility', label: 'Agility Equipment', icon: Dog },
  { id: 'restrooms', label: 'Restrooms Nearby', icon: CheckCircle },
  { id: 'wifi', label: 'WiFi Available', icon: Wifi },
];

const SURFACE_TYPES = [
  { value: 'grass', label: 'Grass' },
  { value: 'gravel', label: 'Gravel' },
  { value: 'mulch', label: 'Wood Chips/Mulch' },
  { value: 'dirt', label: 'Dirt/Sand' },
  { value: 'turf', label: 'Artificial Turf' },
  { value: 'mixed', label: 'Mixed Surfaces' },
];

export default function SubmitDogPark() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    description: '',
    hours: '',
    website: '',
    phone: '',
    surface_type: '',
    size_acres: '',
    entry_fee: '',
    rules: '',
    additional_notes: ''
  });
  
  const [selectedAmenities, setSelectedAmenities] = useState([]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleAmenity = (amenityId) => {
    setSelectedAmenities(prev => 
      prev.includes(amenityId) 
        ? prev.filter(id => id !== amenityId)
        : [...prev, amenityId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.city || !formData.state) {
      toast.error('Please fill in required fields (name, city, state)');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/api/submissions/dog-park`, {
        ...formData,
        amenities: selectedAmenities,
        size_acres: formData.size_acres ? parseFloat(formData.size_acres) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubmitted(true);
      toast.success('Thank you! Your dog park suggestion has been submitted for review.');
    } catch (error) {
      console.error('Error submitting park:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-muted/30 py-12" data-testid="park-submission-success">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-heading font-medium mb-4">Park Submitted!</h1>
          <p className="text-muted-foreground mb-8">
            Thank you for helping us grow our dog park directory! Your submission will be 
            reviewed by our team and added to the map once verified.
          </p>
          <div className="flex gap-4 justify-center">
            <Button variant="outline" onClick={() => { setSubmitted(false); setFormData({ name: '', address: '', city: '', state: '', postal_code: '', country: 'US', description: '', hours: '', website: '', phone: '', surface_type: '', size_acres: '', entry_fee: '', rules: '', additional_notes: '' }); setSelectedAmenities([]); }}>
              Submit Another Park
            </Button>
            <Button asChild>
              <Link to="/parks">Browse Dog Parks</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Login required
  if (!user) {
    return (
      <div className="min-h-screen bg-muted/30 py-12" data-testid="park-submission-login">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <TreePine className="w-16 h-16 mx-auto text-green-600 mb-4" />
          <h1 className="text-2xl font-heading font-medium mb-4">Sign In Required</h1>
          <p className="text-muted-foreground mb-8">
            Please sign in to suggest a dog park. This helps us maintain quality submissions.
          </p>
          <Button asChild>
            <Link to="/login?redirect=/submit-park">Sign In to Continue</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="submit-dog-park-page">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/parks" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dog Parks
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <TreePine className="w-8 h-8 text-green-600" />
            <h1 className="text-2xl font-heading font-medium">Suggest a Dog Park</h1>
          </div>
          <p className="text-muted-foreground">
            Know a great dog park we're missing? Help us grow our directory by submitting it below.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Park Information</CardTitle>
              <CardDescription>Basic details about the dog park</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Park Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Central Bark Dog Park"
                  required
                  data-testid="park-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Tell us about this park - what makes it special?"
                  rows={3}
                  data-testid="park-description-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Location */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Location
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="address">Street Address</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="123 Park Ave"
                  data-testid="park-address-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="City"
                    required
                    data-testid="park-city-input"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="CA"
                    required
                    data-testid="park-state-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="postal_code">ZIP/Postal Code</Label>
                  <Input
                    id="postal_code"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleInputChange}
                    placeholder="90210"
                    data-testid="park-zip-input"
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="US"
                    data-testid="park-country-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Amenities */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Amenities</CardTitle>
              <CardDescription>Select all amenities available at this park</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {AMENITIES.map(amenity => {
                  const Icon = amenity.icon;
                  const isSelected = selectedAmenities.includes(amenity.id);
                  return (
                    <button
                      key={amenity.id}
                      type="button"
                      onClick={() => toggleAmenity(amenity.id)}
                      className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-colors ${
                        isSelected 
                          ? 'bg-green-50 border-green-500 text-green-700' 
                          : 'bg-background hover:bg-muted border-border'
                      }`}
                      data-testid={`amenity-${amenity.id}`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm">{amenity.label}</span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Additional Details */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Additional Details</CardTitle>
              <CardDescription>Optional but helpful information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="hours">Hours of Operation</Label>
                  <Input
                    id="hours"
                    name="hours"
                    value={formData.hours}
                    onChange={handleInputChange}
                    placeholder="e.g., Dawn to Dusk"
                    data-testid="park-hours-input"
                  />
                </div>
                <div>
                  <Label htmlFor="entry_fee">Entry Fee</Label>
                  <Input
                    id="entry_fee"
                    name="entry_fee"
                    value={formData.entry_fee}
                    onChange={handleInputChange}
                    placeholder="e.g., Free, $5/day"
                    data-testid="park-fee-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="surface_type">Surface Type</Label>
                  <select
                    id="surface_type"
                    name="surface_type"
                    value={formData.surface_type}
                    onChange={handleInputChange}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="park-surface-select"
                  >
                    <option value="">Select surface...</option>
                    {SURFACE_TYPES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="size_acres">Size (acres)</Label>
                  <Input
                    id="size_acres"
                    name="size_acres"
                    type="number"
                    step="0.1"
                    value={formData.size_acres}
                    onChange={handleInputChange}
                    placeholder="e.g., 2.5"
                    data-testid="park-size-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="website">Website or Social Media</Label>
                <Input
                  id="website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="https://..."
                  data-testid="park-website-input"
                />
              </div>
              
              <div>
                <Label htmlFor="rules">Park Rules (if known)</Label>
                <Textarea
                  id="rules"
                  name="rules"
                  value={formData.rules}
                  onChange={handleInputChange}
                  placeholder="e.g., Dogs must be vaccinated, no aggressive dogs, clean up after your pet..."
                  rows={2}
                  data-testid="park-rules-input"
                />
              </div>
              
              <div>
                <Label htmlFor="additional_notes">Additional Notes</Label>
                <Textarea
                  id="additional_notes"
                  name="additional_notes"
                  value={formData.additional_notes}
                  onChange={handleInputChange}
                  placeholder="Anything else we should know? Best times to visit, parking tips, etc."
                  rows={2}
                  data-testid="park-notes-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/parks')}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-green-600 hover:bg-green-700"
              data-testid="submit-park-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <TreePine className="w-4 h-4 mr-2" />
                  Submit Park
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Info Box */}
        <Card className="mt-8 bg-blue-50/50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              <strong>What happens next?</strong> Our team will review your submission and verify 
              the park details. Once approved, it will appear on our dog park map. This usually 
              takes 1-3 days. Thank you for helping the pet community!
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
